import React from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { IRootState } from '../../../store/reducer';
import { PlayerPropSportsbook, SportsbookOutcome } from '../data/types';
import { convertSportsbookOutcome } from '../data/transformPropsData';
import { fetchPropsData } from '../data/propsApi';
import { fetchAllSportsbooks } from '../data/stateSportsbooksApi';
import { useAuth } from '../../../hooks/useAuth';
import { usePropsAccess } from './usePropsAccess';

type AllBooksLookup = Record<string, PlayerPropSportsbook[]>;

/**
 * Shared hook that fetches ALL sportsbook odds (ignoring the user's sportsbook filter).
 * Returns a lookup function to get unfiltered sportsbooks for a specific player/stat/side.
 * The query is cached and shared across all components that call this hook.
 */
export function useAllBooksSportsbooks() {
  const { getToken } = useAuth();
  const { isTeaser } = usePropsAccess();
  const filters = useSelector((s: IRootState) => s.stokasticProps.filters);

  // react-query v4: a disabled query sits in status 'loading' forever, so `isLoading` would be true
  // for teaser viewers. `isInitialLoading` is only true while an actual fetch is in flight.
  const { data: allBooksLookup, isInitialLoading: isLoading } = useQuery({
    queryKey: ['props-all-books', filters.league, filters.state, filters.stokWeight, filters.marketWeight],
    // Non-subscribers can't see other books, and the proxy would strip them anyway — skip the
    // second full props request and let callers fall back to the row's own (filtered) books.
    enabled: !isTeaser,
    queryFn: async () => {
      const accessToken = await getToken();
      const allBooks = await fetchAllSportsbooks();
      const allBookCodes = allBooks
        .filter(sb => sb.operatorType === 'Sportsbook')
        .map(sb => sb.sportsbookCode);
      if (allBookCodes.length === 0) return null;
      const result = await fetchPropsData({
        leagues: [filters.league],
        state: filters.state || 'IL',
        sportsbooks: allBookCodes,
        accessToken,
        stokWeight: filters.stokWeight,
        marketWeight: filters.marketWeight,
      });

      if (!result.success || !result.data) return null;

      // Build a lookup: "playerName|stat|side" -> PlayerPropSportsbook[]
      const lookup: AllBooksLookup = {};
      for (const outcome of result.data.outcomes) {
        const key = `${outcome.playerName}|${outcome.stat}|${outcome.label}`;
        const sportsbooks = (outcome.sportsbooks as SportsbookOutcome[])
          .map(convertSportsbookOutcome)
          .sort((a, b) => b.evPct - a.evPct);
        lookup[key] = sportsbooks;
      }
      return lookup;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000,
  });

  const getSportsbooks = React.useCallback(
    (playerName: string, originalStat: string, side: 'Over' | 'Under', fallback: PlayerPropSportsbook[]) => {
      if (!allBooksLookup) return fallback;
      const sideLabel = side === 'Over' ? 'Over' : 'Under';
      const key = `${playerName}|${originalStat}|${sideLabel}`;
      return allBooksLookup[key] || fallback;
    },
    [allBooksLookup],
  );

  return { getSportsbooks, isLoading };
}
